import Log from 'core/src/log';
import EventInterface from 'core/src/event-interface';
import deferredPromise from 'core/src/utils/deferred-promise';
import ApiClientAbstract from 'core/src/api-client-abstract';
import ISession from 'core/src/i-session';
import {def} from 'utils/src/validation';
import Err from 'utils/src/error';
import {v4 as uuid} from 'uuid';

const log = Log.instance("client/core/session");

/**
 * The backends the session can be persisted in. `auto` is not a backend of its own: it resolves
 * to one of the others based on where the client is hosted (see Session.getStorage).
 * @type {Object}
 */
const Storage = {
	AUTO: 'auto',
	COOKIE: 'cookie',
	LOCAL: 'local',
	SESSION: 'session',
	MEMORY: 'memory'
};

/**
 * The order in which the backends are searched when a value is not in the primary one. A session
 * stored under a different setting - by an earlier version of this client, or by a page that
 * configured the client differently - is still found, so a value written anywhere is read back
 * regardless of which backend is currently in use.
 * @type {string[]}
 */
const READ_ORDER = [Storage.COOKIE, Storage.LOCAL, Storage.SESSION, Storage.MEMORY];

/**
 * Prefix for keys in localStorage/sessionStorage, which are shared with the hosting page.
 * @type {string}
 */
const STORAGE_PREFIX = 'graphileon.';

export default class Session extends ISession {
	/**
	 * @param {Dependencies} dependencies
	 * @param {string|boolean} [storage]	Where to persist the session: 'cookie', 'local'
	 * 									(localStorage), 'session' (sessionStorage), 'memory', or
	 * 									'auto' (default). `auto` uses cookies when the page and the
	 * 									server share an origin, and localStorage when they do not:
	 * 									a cookie written on a hosting page belongs to that page's
	 * 									origin and is never sent to the server, which authenticates
	 * 									by `X-Token` request header there anyway.
	 * 									Booleans are accepted for backward compatibility: `true`
	 * 									means 'cookie', `false' means 'local'.
	 */
	constructor(dependencies, storage = Storage.AUTO) {
		super();
		this.id = uuid();
		this._eventInterface = new EventInterface();
		this.on = this._eventInterface.getOnMethod();
		this.fire = this._eventInterface.getFireMethod();

		this.user = null;
		this.storage = Session.normalizeStorage(storage);
		this._memoryStore = {};

		this.api = dependencies.get(ApiClientAbstract);

		this.initFromStorage();
		// Hand the restored token to the API client, so it is sent on subsequent requests.
		this.api.setToken(this.token);

		this.api.on(ApiClientAbstract.Event.SESSION_EXPIRED, ()=>{
			this.clearStoredSession();
		});
		this.api.on(ApiClientAbstract.Event.TOKEN_REFRESHED, (token)=>{
			// Server extended the token's expiry; store it so it survives a reload.
			this.setToken(token);
		});
	}

	initFromStorage() {
		this.getUser();
		this.getToken();
		this.getLanguage();
	}

	/**
	 * @deprecated Use initFromStorage() instead.
	 */
	initFromCookies() {
		return this.initFromStorage();
	}

	/**
	 * Interpret a storage setting, accepting the booleans the older `useCookies` API used.
	 * @param {string|boolean} [storage]
	 * @returns {string}	One of the Storage values; 'auto' for anything unrecognised.
	 */
	static normalizeStorage(storage) {
		if (storage === true) {
			return Storage.COOKIE;
		}
		// `false` used to mean sessionStorage. localStorage is the better fit for the case it was
		// meant for - a client hosted on another origin - because sessionStorage is per tab, so
		// every new tab would start without a session and have to log in again.
		if (storage === false) {
			return Storage.LOCAL;
		}
		if (typeof storage === 'string' && READ_ORDER.indexOf(storage) >= 0) {
			return storage;
		}
		return Storage.AUTO;
	}

	/**
	 * The backend values are written to: the configured one, or what `auto` resolves to. Resolved
	 * per call rather than once, because the server the client talks to is often configured after
	 * the session is constructed (`api.setConfig()` in a hosting page).
	 * @returns {string}	One of the Storage values, never 'auto'.
	 */
	getStorage() {
		if (this.storage !== Storage.AUTO) {
			return this._firstAvailableStorage([this.storage]);
		}
		// Cookies only reach the server when the page shares its origin. Written anywhere else
		// they belong to the hosting page, so browser storage is used instead.
		if (! this._isCrossOriginServer()) {
			return this._firstAvailableStorage([Storage.COOKIE]);
		}
		return this._firstAvailableStorage([Storage.LOCAL, Storage.SESSION]);
	}

	/**
	 * Switch the storage backend after construction, migrating whatever is already stored.
	 * Use this when the client is constructed for you (e.g. by a bundle loaded in a hosting page)
	 * and you cannot pass `storage` to its constructor. Call it before the user logs in.
	 * @param {string|boolean} storage	See the constructor.
	 * @returns {undefined}
	 */
	setStorage(storage) {
		storage = Session.normalizeStorage(storage);
		if (storage === this.storage) {
			return;
		}

		// Read before erasing: readValue searches every backend, so this picks up a session that
		// a previous page load stored under either the old or the new setting.
		const token = this.readValue('token');
		const language = this.readValue('language');

		this.eraseEverywhere('token');
		this.eraseEverywhere('language');

		this.storage = storage;

		// Re-store under the new backend.
		if (token) {
			this.storeValue('token', token, 24);
		}
		if (language) {
			this.storeValue('language', language, 24);
		}

		this.token = token || null;
		this.api.setToken(this.token);
	}

	/**
	 * @deprecated Use setStorage() instead.
	 * @param {boolean} useCookies	True to store the session in cookies, false for localStorage.
	 * @returns {undefined}
	 */
	setUseCookies(useCookies) {
		return this.setStorage(useCookies !== false);
	}

	/**
	 * The first of the given backends that can actually be used here, falling back to the
	 * in-memory store (which always works, but does not survive a reload).
	 * @param {string[]} backends
	 * @returns {string}
	 */
	_firstAvailableStorage(backends) {
		for (const backend of backends) {
			if (backend === Storage.MEMORY) {
				return backend;
			}
			if (backend === Storage.COOKIE) {
				if (typeof document !== 'undefined') {
					return backend;
				}
				continue;
			}
			if (this._getWebStorage(backend)) {
				return backend;
			}
		}
		return Storage.MEMORY;
	}

	/**
	 * Whether the server the API client talks to is on another origin than the page. Relative or
	 * unset server URLs mean the server is the page's own origin.
	 * @returns {boolean}
	 */
	_isCrossOriginServer() {
		if (typeof window === 'undefined' || ! window.location) {
			return false;
		}
		const server = this.api.getServer();
		if (! server) {
			return false;
		}
		try {
			return new URL(server, window.location.href).origin !== window.location.origin;
		} catch (e) {
			return false; // an unparseable server URL is no reason to give up on cookies
		}
	}

	/**
	 * The localStorage or sessionStorage of the hosting page, or null when it is unavailable
	 * (e.g. disabled by the browser, or running outside a browser). Callers fall back to an
	 * in-memory store.
	 * @param {string} backend	Storage.LOCAL or Storage.SESSION.
	 * @returns {Storage|null}
	 */
	_getWebStorage(backend) {
		try {
			if (typeof window === 'undefined') {
				return null;
			}
			return (backend === Storage.LOCAL ? window.localStorage : window.sessionStorage) || null;
		} catch (e) {
			return null; // access can throw when storage is blocked
		}
	}

	/**
	 * @deprecated Use _getWebStorage() instead.
	 * @returns {Storage|null}
	 */
	_getSessionStorage() {
		return this._getWebStorage(Storage.SESSION);
	}

	/**
	 * Resolve a backend argument, so every public method accepts the same values as the
	 * constructor and defaults to the backend currently in use.
	 * @param {string|boolean} [backend]
	 * @returns {string}
	 */
	_resolveStorage(backend) {
		const normalized = Session.normalizeStorage(backend);
		return normalized === Storage.AUTO ? this.getStorage() : normalized;
	}

	/**
	 * Browser storage has no expiry of its own, so the expiry a cookie would get from `hours` is
	 * written into the value and checked on read.
	 * @param {string} value
	 * @param {int} [hours]
	 * @returns {string}
	 */
	_packValue(value, hours) {
		if (! hours) {
			return value;
		}
		return JSON.stringify({v: value, exp: Date.now() + hours * 3600000});
	}

	/**
	 * Counterpart of _packValue. Values written without an expiry (and by earlier versions of
	 * this client) are stored as-is and returned unchanged.
	 * @param {string} raw
	 * @returns {string|undefined}	undefined when the value has expired.
	 */
	_unpackValue(raw) {
		if (typeof raw !== 'string' || raw.charAt(0) !== '{') {
			return raw;
		}
		let packed;
		try {
			packed = JSON.parse(raw);
		} catch (e) {
			return raw; // not ours; a plain value that happens to start with a brace
		}
		if (! packed || typeof packed !== 'object' || ! ('v' in packed)) {
			return raw;
		}
		if (packed.exp && packed.exp <= Date.now()) {
			return undefined;
		}
		return packed.v;
	}

	/**
	 * Store a session value in the given backend.
	 * @param {string} name
	 * @param {string} value
	 * @param {int} [hours]	Expiry.
	 * @param {string|boolean} [backend]	Backend to write to; defaults to the one in use.
	 */
	storeValue(name, value, hours, backend) {
		backend = this._resolveStorage(backend);
		if (backend === Storage.COOKIE) {
			return this.createCookie(name, value, hours);
		}
		if (backend === Storage.MEMORY) {
			this._memoryStore[name] = value;
			return;
		}
		const storage = this._getWebStorage(backend);
		if (! storage) {
			this._memoryStore[name] = value;
			return;
		}
		try {
			storage.setItem(STORAGE_PREFIX + name, this._packValue(value, hours));
		} catch (e) {
			log.warn(`Could not persist '${name}' in ${backend} storage; keeping it in memory.`);
			this._memoryStore[name] = value;
		}
	}

	/**
	 * Read a session value from wherever it was stored: the backend currently in use first, then
	 * the others, so a value stored under a different setting is still found.
	 * @param {string} name
	 * @param {string|boolean} [backend]	Read from this backend only.
	 * @returns {string|null}
	 */
	readValue(name, backend) {
		if (backend !== undefined) {
			return this._readValueFrom(name, this._resolveStorage(backend));
		}

		const primary = this.getStorage();
		const value = this._readValueFrom(name, primary);
		if (value !== null) {
			return value;
		}
		for (const other of READ_ORDER) {
			if (other === primary) {
				continue;
			}
			const stored = this._readValueFrom(name, other);
			if (stored !== null) {
				return stored;
			}
		}
		return null;
	}

	/**
	 * @param {string} name
	 * @param {string} backend
	 * @returns {string|null}
	 */
	_readValueFrom(name, backend) {
		if (backend === Storage.COOKIE) {
			return this.readCookie(name);
		}
		if (backend === Storage.MEMORY) {
			return this._memoryStore[name] !== undefined ? this._memoryStore[name] : null;
		}
		const storage = this._getWebStorage(backend);
		if (! storage) {
			return null;
		}
		let raw;
		try {
			raw = storage.getItem(STORAGE_PREFIX + name);
		} catch (e) {
			return null;
		}
		if (raw === null || raw === undefined) {
			return null;
		}
		const value = this._unpackValue(raw);
		if (value === undefined) {
			this.eraseValue(name, backend); // expired, so clean it up on the way out
			return null;
		}
		return value;
	}

	/**
	 * Remove a stored session value.
	 * @param {string} name
	 * @param {string|boolean} [backend]	Backend to erase from; defaults to the one in use.
	 */
	eraseValue(name, backend) {
		backend = this._resolveStorage(backend);
		if (backend === Storage.COOKIE) {
			return this.eraseCookie(name);
		}
		if (backend === Storage.MEMORY) {
			delete this._memoryStore[name];
			return;
		}
		const storage = this._getWebStorage(backend);
		if (! storage) {
			return;
		}
		try {
			storage.removeItem(STORAGE_PREFIX + name);
		} catch (e) {
			// nothing left to do
		}
	}

	/**
	 * Create/modify a cookie
	 * @param {string} name
	 * @param {string} value
	 * @param {int} hours
	 * @returns {undefined}
	 */
	createCookie(name,value,hours) {
		if (typeof document === 'undefined') return; // no cookies outside a browser
		if (hours) {
			var date = new Date();
			date.setTime(date.getTime()+(hours*60*60*1000));
			var expires = "; expires="+date.toGMTString();
		}
		else var expires = "";
		document.cookie = name+"="+value+expires+"; path=/"+this.cookieAttributes();
	};

	/**
	 * The attributes the session cookie is written with, matching the ones the server uses
	 * (`config.cookieOptions`, which defaults to `secure` + `SameSite=None` on https).
	 *
	 * They matter for deletion as much as for creation: a cookie is only replaced by a write
	 * carrying the same attributes, so erasing without them can leave the server's cookie in
	 * place. Plain http stays on the browser default, since `SameSite=None` is only accepted on
	 * secure cookies.
	 * @returns {string}
	 */
	cookieAttributes() {
		const secure = typeof location !== 'undefined' && location.protocol === 'https:';
		return secure ? "; SameSite=None; Secure" : "";
	}

	/**
	 * Read a cookie
	 * @param {string} name
	 * @returns {undefined}
	 */
	readCookie(name) {
		if (typeof document === 'undefined') return null; // no cookies outside a browser
		var nameEQ = name + "=";
		var ca = document.cookie.split(';');
		for(var i=0;i < ca.length;i++) {
			var c = ca[i];
			while (c.charAt(0)==' ') c = c.substring(1,c.length);
			if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
		}
		return null;
	}

	/**
	 * Erase a cookie
	 * @param {string} name
	 * @returns {undefined}
	 */
	eraseCookie(name) {
		if (typeof document === 'undefined') return; // no cookies outside a browser
		// `max-age=0` is what current browsers act on; `expires` in the past covers the rest.
		document.cookie = `${name}=; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${this.cookieAttributes()}`;
	}

	async clear() {
		this.user = null;
		this.token = null;
		this.language = null;

		// Erase the stored session before the request rather than after: that request goes over
		// the network and may be slow, fail or time out, and until it returns the token would
		// still be in storage - long enough for a reload to log the user straight back in.
		// The API client keeps its token for now, so the request can still tell the server which
		// session to invalidate (when authenticating by header there is nothing else to go on).
		this.clearStoredSession();

		try {
			await this.api.logout();
		} catch (err) {
			log.error(err);
		}
		// Also drop the token from the API client when the logout request itself failed, so no
		// stale token is sent on subsequent requests.
		this.api.setToken(null);

		// The server refreshes the session cookie on every response, including the one just
		// awaited and any request that was still in flight, so erase it once more now that the
		// session is gone and no response can restore it.
		this.clearStoredSession();
	}

	/**
	 * Clear the stored session.
	 * Clears every storage backend, not just the one currently in use: a session may have been
	 * stored under a different `useCookies` setting (or by an older version of this client), and
	 * leaving a stale token behind would keep it valid until it expires server-side.
	 * @returns {undefined}
	 */
	clearStoredSession() {
		this.eraseEverywhere('token');
		this.eraseEverywhere('language');
	}

	/**
	 * Remove a stored value from all storage backends: cookies, localStorage, sessionStorage and
	 * memory. Reads search every backend, so a value left behind in any of them would come back.
	 * @param {string} name
	 */
	eraseEverywhere(name) {
		for (const backend of READ_ORDER) {
			this.eraseValue(name, backend);
		}
	}

	/**
	 * @deprecated Use clearStoredSession() instead.
	 * @returns {undefined}
	 */
	clearAllCookies() {
		return this.clearStoredSession();
	};

	/**
	 * Attempt a user login
	 * @param {string} username
	 * @param {string} password
	 * @param {boolean} [cookies] Store session as cookies?
	 * @returns {Deferred}
	 */
	authenticate(username, password, cookies, tfaToken, automatic) {
		let promise = new Promise(async (resolve, reject) => {
			// Already authenticated and no new username/password provided, just resolve with current user
			if(!username && !password && this.token && this.user) {
				return resolve({user: this.user, token: this.token});
			}

			// No user info, but we have a token
			if(!username && !password && this.getToken()) {
				let user = await this.api.getCurrentUser();
				let profiles =  await this.api.getCurrentProfiles();
				user.properties.profiles = profiles;
				user.properties.authenticator = await this.api.getCurrentAuthenticator();

				this.setUser(user);
				return resolve({user, token: this.token});
			}

			if(cookies === undefined) {
				cookies = true;
			}

			if ( ! username || ! password ) {
				this.clearStoredSession();
				return reject(!automatic ? new Err("Username and password required") : '');
			}

			const request = this.api.login(username, password, tfaToken);
			request.then(async response => {
				if (response.user === undefined) {
					return reject(new Err("Could not log in."));
				}

				const token = response.token;
				this.setToken(token);

				let user = response.user;
				user.properties.profiles =  await this.api.getCurrentProfiles();
				user.properties.authenticator = await this.api.getCurrentAuthenticator();

				this.setUser(user);

				this.fire(Session.Event.LOGIN, {user, token});

				resolve({user, token});
			}).catch(error => {
				const code = error.code;
				const message = error.message;
				this.clear();
				reject(new Err({code: code, message: message}));
			});
		});
		return deferredPromise(promise);
	}




	/**
	 * Sets the user of the current session
	 * @param user
	 */
	setUser(user) {
		this.user = user;
	}

	/**
	 * Sets the language for this session.
	 * @param language
	 */
	async setLanguage(language) {
		this.language = language;
		this.storeValue('language', language, 24);
	}

	/**
	 * Sets the access token for this session.
	 * @param token
	 */
	setToken(token) {
		this.token = token;
		if (token) {
			this.storeValue('token', token, 24);
		} else {
			this.eraseEverywhere('token');
		}
		this.api.setToken(token);
	}

	getUser() {
		return this.user;
	}

	getToken() {
		this.token = this.readValue('token');
		return this.token;
	}

	/**
	 * The language stored for this session, or null when the user never picked one. Callers fall
	 * back to the server's configured default language rather than to a language of their own:
	 * returning one here would silently override `defaultLanguage`.
	 * @returns {string|null}
	 */
	getLanguage() {
		this.language = this.readValue('language') || null;
		return this.language;
	}

	/**
	 * @deprecated Use logout() instead.
	 * @returns {*}
	 */
	logOffUser() {
		return this.logout();
	}
	async logout() {
		const user = this.getUser();
		try {
			await this.clear();
		} catch (err) {
			log.error(err);
		}
		this.fire(Session.Event.LOGOUT, user);
	}

	isLoggedIn() {
		return this.user && def(this.user.id);
	}
}
